import React, { useState, useEffect, useRef } from 'react';
import { Layout, Row, Col, Card, Typography, Spin, Alert, message as antdMessage } from 'antd';
import AppHeader from '../components/Layout/AppHeader';
import MrpUpload from '../components/Dashboard/MrpUpload';
import BomUpload from '../components/Dashboard/BomUpload';
import WerkSelection from '../components/Dashboard/WerkSelection';
import RootProductForm from '../components/Dashboard/RootProductForm';
import DeadlineForm from '../components/Dashboard/DeadlineForm';
import GanttChartDisplay from '../components/Dashboard/GanttChartDisplay';
import CurrentFilesDisplay from '../components/Dashboard/CurrentFilesDisplay';
import DebugPanel from '../components/Dashboard/DebugPanel';
import FilteredItemsTable from '../components/Dashboard/FilteredItemsTable';
import { 
  MrpUploadApiResponse, 
  BomUploadApiResponse, 
  AddRootApiResponse, 
  WerkSelectionApiResponse,
  MrpData, 
  ProcessedBomData, 
  fetchGanttChartData, 
  GanttChartApiResponse 
} from '../services/api';

const { Content } = Layout;
const { Title } = Typography;

const DashboardPage: React.FC = () => {
  // MRP workflow state
  const [mrpResponse, setMrpResponse] = useState<MrpUploadApiResponse | null>(null);
  const [selectedWerkData, setSelectedWerkData] = useState<WerkSelectionApiResponse | null>(null);
  
  // Legacy state for compatibility
  const [mrpData, setMrpData] = useState<MrpData | null>(null);
  const [mrpFilename, setMrpFilename] = useState<string | null>(null);
  
  const [bomData, setBomData] = useState<ProcessedBomData | null>(null);
  const [deadline, setDeadline] = useState<number | null>(null);
  
  const [chartFigure, setChartFigure] = useState<{ data: any[], layout: any } | null>(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState<string | null>(null);

  // Ref to store the AbortController for the chart generation request
  const chartAbortControllerRef = useRef<AbortController | null>(null);

  const handleMrpProcessed = (data: MrpUploadApiResponse) => {
    setMrpResponse(data);
    setMrpFilename(data.filename);
    
    // Reset subsequent data if MRP is re-uploaded
    setSelectedWerkData(null);
    setMrpData(null);
    setBomData(null);
    setChartFigure(null);
    
    antdMessage.info('MRP file processed. Please select a Werk to continue.');
  };

  const handleWerkSelected = (data: WerkSelectionApiResponse) => {
    setSelectedWerkData(data);
    setMrpData(data.mrp_data);
    
    // Reset BOM data when Werk changes
    setBomData(null);
    setChartFigure(null);
    
    antdMessage.success(`Werk "${data.selected_werk}" selected. You can now upload the BOM file.`);
  };

  const handleBomProcessed = (data: BomUploadApiResponse) => {
    setBomData(data.bomData);
    // If BOM processed successfully, clear previous chart
    setChartFigure(null);
  };

  const handleRootAdded = (data: AddRootApiResponse) => {
    // Preserve the filtered items from the original BOM data
    const updatedBomData = {
      ...data.bomData,
      filteredItems: bomData?.filteredItems || { zeroTimeItems: [], notInMrpItems: [] }
    };
    setBomData(updatedBomData);
    // If root added, clear previous chart to force regeneration
    setChartFigure(null);
  };

  const handleDeadlineSet = (newDeadline: number | null) => {
    setDeadline(newDeadline);
    // If deadline changes, clear previous chart to force regeneration
    // (only if a chart could potentially be displayed)
    if (bomData && bomData.rootIndex !== null) {
        setChartFigure(null);
    }
  };

  // Effect to generate chart when BOM data (with root) or deadline changes
  useEffect(() => {
    // If a chart generation is already in progress when dependencies change, cancel it
    if (chartAbortControllerRef.current) {
      chartAbortControllerRef.current.abort();
      console.log("Previous chart generation aborted due to bomData/deadline change.");
    }

    if (bomData && bomData.dataframeJson && bomData.rootIndex !== null) {
      // Create a new AbortController for this request
      const controller = new AbortController();
      chartAbortControllerRef.current = controller;

      const generateChart = async () => {
        setChartLoading(true);
        setChartError(null);
        try {
          console.log("Fetching chart data with deadline:", deadline);
          const response = await fetchGanttChartData(
            bomData.dataframeJson,
            bomData.rootIndex as number, // Already checked for null
            deadline,
            controller.signal // Pass the signal to Axios
          );
          // If the request was aborted, chartAbortControllerRef.current would be nullified by the cleanup
          // or by a new request starting. Or, the error handler for AbortError will catch it.
          if (!controller.signal.aborted) { // Check if this specific request was aborted
            if (response.data.error) {
              setChartError(response.data.message || "Fehler bei Diagrammerstellung.");
              setChartFigure(null);
              antdMessage.error(response.data.message || "Fehler bei Diagrammerstellung.");
            } else if (response.data.data && response.data.layout) {
              setChartFigure({ data: response.data.data, layout: response.data.layout });
            } else {
              setChartError("Ungültige Diagrammdaten vom Server erhalten.");
              setChartFigure(null);
            }
          }
        } catch (err: any) {
          if (err.name === 'AbortError' || err.name === 'CanceledError') { // Axios might throw CanceledError
            console.log('Chart generation request was aborted.');
            // No need to set error state for user for an abort, it's intentional
            // But ensure loading is false if it was the last action
            // If a new request starts immediately, setChartLoading(true) will be called by it.
          } else if (!controller.signal.aborted) { // Only set error if not aborted
            const apiError = err.response?.data?.detail || err.message || 'Failed to fetch chart data.';
            setChartError(apiError);
            setChartFigure(null);
            antdMessage.error(apiError);
          }
        } finally {
            // Only set loading to false if this specific request (controller) is the one that finished
            // and it wasn't aborted by a newer request.
            if (chartAbortControllerRef.current === controller && !controller.signal.aborted) {
                setChartLoading(false);
            } else if (controller.signal.aborted && chartAbortControllerRef.current !== controller) {
                // This means this request was aborted, and a new one started.
                // The new one will handle setChartLoading(true) then setChartLoading(false).
                // So, do nothing here for loading.
            } else if (controller.signal.aborted && chartAbortControllerRef.current === controller) {
                // This means this request was aborted, and NO new one has started yet.
                setChartLoading(false);
            }
        }
      };

      generateChart();

      // Cleanup function for useEffect
      return () => {
        console.log("useEffect cleanup: Aborting chart generation if still active.");
        controller.abort(); // Abort this request if component unmounts or deps change
        if (chartAbortControllerRef.current === controller) {
          chartAbortControllerRef.current = null; // Clear the ref if it's this controller
        }
      };
    } else {
      setChartFigure(null); // Clear chart if no valid BOM data
      setChartLoading(false); // Ensure loading is off
      setChartError(null);
      if (chartAbortControllerRef.current) { // Abort any lingering request
          chartAbortControllerRef.current.abort();
          chartAbortControllerRef.current = null;
      }
    }
  }, [bomData, deadline]); // Dependencies that trigger chart regeneration

  const werkSelectionDisabled = !mrpResponse;
  const bomUploadDisabled = !mrpData;
  const deadlineFormDisabled = !bomData || bomData.rootIndex === null;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 3. Render the AppHeader */}
      <AppHeader />
      
      {/* 4. Place your original page content inside the <Content> component */}
      <Content style={{ padding: '24px' }}>
        <Title level={2}>PBA Dashboard</Title>
        
        {/* All your existing <Row>, <Col>, and <Card> components go here */}
        <Row gutter={[24, 24]}>
          <Col xs={24}>
            <Card title="Schritt 1: MRP-Parameter Datei hochladen">
              <MrpUpload onMrpProcessed={handleMrpProcessed} />
            </Card>
          </Col>
        </Row>

        {mrpResponse && (
          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col xs={24}>
              <Card title="Schritt 2: Werk auswählen">
                <WerkSelection
                  availableWerks={mrpResponse.available_werks}
                  mrpDataframeJson={mrpResponse.mrp_dataframe_json}
                  onWerkSelected={handleWerkSelected}
                  disabled={werkSelectionDisabled}
                />
              </Card>
            </Col>
          </Row>
        )}

        {selectedWerkData && (
          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col xs={24}>
              <Card title="Schritt 3: Stückliste Datei hochladen">
                <Alert
                  message={`Werk "${selectedWerkData.selected_werk}" ausgewählt`}
                  description="BOM wird mit den Zeiten für das ausgewählte Werk verarbeitet."
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <BomUpload
                  mrpData={mrpData}
                  onBomProcessed={handleBomProcessed}
                  disabled={bomUploadDisabled}
                />
              </Card>
            </Col>
          </Row>
        )}

        <CurrentFilesDisplay 
          mrpFilename={mrpFilename} 
          bomFilename={bomData?.filename} 
        />

        {bomData && bomData.rootIndex === null && (
          <RootProductForm currentBomData={bomData} onRootAdded={handleRootAdded} />
        )}
        
        <DeadlineForm 
          onDeadlineSet={handleDeadlineSet} 
          currentDeadline={deadline} 
          disabled={deadlineFormDisabled}
        />

        <FilteredItemsTable 
          filteredItems={bomData?.filteredItems || { zeroTimeItems: [], notInMrpItems: [] }}
          visible={bomData !== null}
        />

        <Card title="Visualisierung" style={{ marginTop: 24 }}>
          <GanttChartDisplay
            chartData={chartFigure?.data}
            chartLayout={chartFigure?.layout}
            loading={chartLoading}
            error={chartError}
          />
        </Card>
        
      </Content>
    </Layout>
  );
};

export default DashboardPage;